// Drawing Guess furniture: a big studio easel (~2.5 m wide frame on an
// A-frame stand, with a marker tray). The live drawing is laid over the board
// by DrawingTable3D — this is decoration only.
//
// Built from primitives for now. To swap in a Blender model later, export it
// with the board's front face matching easelLayout (BOARD_* / FRAME_W, facing
// +Z, floor at 0) to public/models/games/drawing-easel.glb and set
// EASEL_GLB_URL — the primitive easel stays as the loading / error fallback.

import React, { Component, Suspense, useMemo } from 'react';
import * as THREE from 'three';
import { useGLTF } from '@react-three/drei';
import { BOARD_BOTTOM, BOARD_H, BOARD_TOP, BOARD_W, BOARD_Z, FRAME_BORDER, FRAME_W } from './easelLayout';
import { COLORS } from './strokes';

/** Set to '/models/games/drawing-easel.glb' once the model exists. */
export const EASEL_GLB_URL: string | null = null;

const WOOD = '#9a6a3a', WOOD_DARK = '#6e4a27', BOARD = '#f7f5ef';
const nc = { collidable: false };

/** A round beam from a to b (floor-origin coordinates). */
function Beam({ a, b, r = 0.035, color = WOOD }: { a: [number, number, number]; b: [number, number, number]; r?: number; color?: string }) {
  const { pos, quat, len } = useMemo(() => {
    const va = new THREE.Vector3(...a), vb = new THREE.Vector3(...b);
    const dir = vb.clone().sub(va);
    const q = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir.clone().normalize());
    return { pos: va.add(vb).multiplyScalar(0.5), quat: q, len: dir.length() };
  }, [a, b]);
  return (
    <mesh position={pos} quaternion={quat} castShadow userData={nc}>
      <cylinderGeometry args={[r, r, len, 10]} />
      <meshStandardMaterial color={color} roughness={0.75} />
    </mesh>
  );
}

export function PrimitiveEasel() {
  const frameD = 0.06;
  const cz = BOARD_Z - frameD / 2;
  const top = BOARD_TOP + FRAME_BORDER;
  const bottom = BOARD_BOTTOM - FRAME_BORDER;
  return (
    <group>
      {/* Board */}
      <mesh position={[0, BOARD_BOTTOM + BOARD_H / 2, cz]} receiveShadow userData={nc}>
        <boxGeometry args={[BOARD_W, BOARD_H, frameD - 0.004]} />
        <meshStandardMaterial color={BOARD} roughness={0.9} />
      </mesh>
      {/* Frame */}
      <mesh position={[0, top - FRAME_BORDER / 2, cz]} castShadow userData={nc}>
        <boxGeometry args={[FRAME_W, FRAME_BORDER, frameD]} /><meshStandardMaterial color={WOOD} roughness={0.7} />
      </mesh>
      <mesh position={[0, bottom + FRAME_BORDER / 2, cz]} castShadow userData={nc}>
        <boxGeometry args={[FRAME_W, FRAME_BORDER, frameD]} /><meshStandardMaterial color={WOOD} roughness={0.7} />
      </mesh>
      {[-1, 1].map((s) => (
        <mesh key={s} position={[s * (FRAME_W - FRAME_BORDER) / 2, BOARD_BOTTOM + BOARD_H / 2, cz]} castShadow userData={nc}>
          <boxGeometry args={[FRAME_BORDER, BOARD_H, frameD]} /><meshStandardMaterial color={WOOD} roughness={0.7} />
        </mesh>
      ))}
      {/* A-frame stand: two front legs, one back leg, a crossbar */}
      <Beam a={[-1.02, bottom + 0.05, cz - 0.02]} b={[-1.18, 0, 0.32]} r={0.04} />
      <Beam a={[1.02, bottom + 0.05, cz - 0.02]} b={[1.18, 0, 0.32]} r={0.04} />
      <Beam a={[0, top - 0.1, cz - 0.04]} b={[0, 0, -0.85]} r={0.04} color={WOOD_DARK} />
      <Beam a={[-1.1, 0.3, 0.18]} b={[1.1, 0.3, 0.18]} r={0.025} />
      {/* Marker tray along the bottom of the frame */}
      <mesh position={[0, bottom - 0.02, BOARD_Z + 0.07]} castShadow userData={nc}>
        <boxGeometry args={[FRAME_W - 0.2, 0.03, 0.16]} /><meshStandardMaterial color={WOOD} roughness={0.7} />
      </mesh>
      <mesh position={[0, bottom + 0.015, BOARD_Z + 0.145]} userData={nc}>
        <boxGeometry args={[FRAME_W - 0.2, 0.04, 0.012]} /><meshStandardMaterial color={WOOD_DARK} roughness={0.7} />
      </mesh>
      {/* Markers in the palette colours */}
      {COLORS.map((c, i) => (
        <mesh key={c} position={[-0.6 + i * 0.17, bottom + 0.012, BOARD_Z + 0.07]} rotation={[0, 0, Math.PI / 2]} userData={nc}>
          <cylinderGeometry args={[0.014, 0.014, 0.13, 10]} />
          <meshStandardMaterial color={c} roughness={0.5} />
        </mesh>
      ))}
    </group>
  );
}

function EaselGLB({ url }: { url: string }) {
  const { scene } = useGLTF(url);
  const model = useMemo(() => {
    const root = scene.clone(true);
    root.traverse((c) => {
      c.userData.collidable = false;
      const m = c as THREE.Mesh;
      if (m.isMesh) { m.castShadow = true; m.receiveShadow = true; }
    });
    return root;
  }, [scene]);
  return <primitive object={model} />;
}

class EaselBoundary extends Component<{ fallback: React.ReactNode; children: React.ReactNode }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch(err: unknown) { console.warn('[drawing] easel model failed, using the simple easel', err); }
  render() { return this.state.failed ? this.props.fallback : this.props.children; }
}

/** The easel standing on the floor (registry `Furniture`). */
export function DrawingEasel() {
  const fallback = <PrimitiveEasel />;
  if (!EASEL_GLB_URL) return fallback;
  return (
    <EaselBoundary fallback={fallback}>
      <Suspense fallback={fallback}><EaselGLB url={EASEL_GLB_URL} /></Suspense>
    </EaselBoundary>
  );
}

export default DrawingEasel;
